import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Close, CheckCircle } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

export const PaymentCyclePopup = ({
  open,
  onClose,
  installments,
  submitPayment,
  countryname,
  coursename,
}) => {
  const [activeRow, setActiveRow] = useState(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    trigger,
  } = useForm({
    defaultValues: {
      payments:
        installments?.map((item) => ({
          installmentamount: item.installmentamount || "",
          paymentdate: item.paymentdate
            ? dayjs(item.paymentdate, "YYYY-MM-DD")
            : null,
        })) || [],
    },
  });

  const handleClose = () => {
    onClose();
  };

  const handleFormSubmit = async (data, index, lifecycleid) => {
    setActiveRow(index);
    const isValid = await trigger([
      `payments.${index-1}.installmentamount`,
      `payments.${index-1}.paymentdate`,
    ]);
    if (isValid) {
      const paymentData = data.payments[index];
      submitPayment({
        installmentamount: paymentData.installmentamount,
        paymentdate: dayjs(paymentData.paymentdate).format("YYYY-MM-DD"),
        lifecycleid: lifecycleid
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderBottom: "0.5px solid #C0C0C0",
          margin: "3px",
          marginBottom: "10px",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#000",
            fontSize: "var(--TypeStyle-H4, 20px)",
            fontWeight: 700,
            lineHeight: "var(--TypeHeight-H6, 29px)",
          }}
        >
          Payment Cycle
        </Typography>
        <IconButton sx={{ color: "black" }} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            borderRadius: "20px",
            background: "var(--Bg-Tonal-Blue, #E1ECFE)",
            p: 2,
            mb: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              sx={{
                color: "#000",
                fontSize: "var(--TypeStyle-H5, 18px)",
                fontWeight: 500,
                lineHeight: "var(--TypeHeight-H5, 29px)",
              }}
            >
              Selected country and course
            </Typography>
            <Typography
              sx={{
                color: "var(--Text-Tonal-Blue, #3A80F7)",
                fontSize: "var(--TypeStyle-H5, 18px)",
                fontWeight: 500,
                lineHeight: "var(--TypeHeight-H5, 29px)",
              }}
            >
              {countryname}, {coursename}
            </Typography>
          </Stack>
        </Box>

        {installments?.map((installment, index) => (
          <Box
            key={index}
            sx={{
              borderRadius: "20px",
              border: "0.5px solid var(--Border-Secondary, #C0C0C0)",
              p: 2,
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              borderBottom="0.5px solid var(--Border-Secondary, #EBEBEB)"
              pb={1}
            >
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "var(--TypeStyle-H5, 18px)",
                  fontWeight: 500,
                  lineHeight: "var(--TypeHeight-H5, 29px)",
                }}
              >
                {`${installment.installmentnumber}. ${installment.eventname}`}
              </Typography>
              {!installment.amount ? (
                <TextField
                  placeholder="Amount"
                  defaultValue={installment.installmentamount}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: "150px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                  {...register(`payments.${index}.installmentamount`, {
                    required: activeRow === index ? "Amount is required" : false,
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    },
                    onBlur: () => trigger(`payments.${index}.installmentamount`),
                  })}
                  error={
                    activeRow === index && errors.payments?.[index]?.installmentamount
                  }
                  helperText={
                    activeRow === index && errors.payments?.[index]?.installmentamount?.message
                  }
                />
              ) : (
                <Typography
                  sx={{
                    color: "var(--Text-Tonal-Blue, #3A80F7)",
                    fontSize: "var(--TypeStyle-H4, 20px)",
                    fontWeight: 700,
                    lineHeight: "var(--TypeHeight-H6, 29px)",
                  }}
                >
                  ₹ {installment.amount}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Controller
                name={`payments.${index}.paymentdate`}
                control={control}
                rules={{
                  required: activeRow === index ? "Date is required" : false,
                }}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    format="LL"
                    defaultValue={
                      installments[index].paymentdate
                        ? dayjs(installments[index].paymentdate)
                        : null
                    }
                    className="inputMdRounded"
                    sx={{
                      bgcolor: "transparent",
                      width: "200px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                    slotProps={{
                      textField: {
                        error: !!(activeRow === index && error),
                        helperText: activeRow === index ? error?.message : "",
                      },
                    }}
                  />
                )}
              />
              {!installment.amount ? (
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid var(--Border-Primary, #3A80F7)",
                    background: "var(--Bg-Tonal-Blue, #E1ECFE)",
                    color: "var(--Text-Tonal-Blue, #3A80F7)",
                    fontSize: "var(--TypeStyle-H5, 18px)",
                    fontWeight: 500,
                    textTransform: "none",
                    width: 150,
                    gap: 8,
                    "&:hover": {
                      border: "1px solid var(--Border-Primary, #3A80F7)",
                      background: "var(--Bg-Tonal-Blue, #E1ECFE)",
                      opacity: 0.9,
                    },
                  }}
                  onClick={() => {
                    setActiveRow(index); // Set active row first
                    handleSubmit((data) => handleFormSubmit(data, index, installment.lifecycleid))();
                  }}
                >
                  Pay
                </Button>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircle sx={{ color: "#4CAF50" }} />
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: "var(--TypeStyle-H5, 18px)",
                      fontWeight: 500,
                      lineHeight: "var(--TypeHeight-H5, 29px)",
                    }}
                  >
                    Paid
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};
